<script>
import pagination from "@/mixins/pagination";
import tableSearch from "@/mixins/tableSearch";
import CommonTableComponent from "@/components/table.vue";
import apiEndpoints from "@/services/apiEndpoints";
import {ZZ_WORK_ORDER_TYPE_SERVICE} from '@/configs/work_order'

export default {
  components: {
    CommonTableComponent
  },
  props: {
    isUpdate: {
      type: Boolean,
      default: false,
    },
    caseId: {
      type: Number,
      default: 0,
    },
    wastesOrServicesByWorkOrder: {
      type: Array,
      default: () => [],
    },
    workOrderTypeId: {
      type: String,
      default: ZZ_WORK_ORDER_TYPE_SERVICE,
    },
    contracts: {
      type: Array,
      default: [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [pagination, tableSearch],
  data() {
    return {
      wastesOrServices: [],
      selectedItems: [],
    };
  },
  methods: {
    async getData() {
      this.isLoading = true;
      const url = apiEndpoints.work_order_create_wastes_or_services(this.caseId);
      const sendApiProject = await this.$http.get(url, {
        work_order_type: this.workOrderTypeId,
        transport_contract_id: this.transportContract?.id,
        treatment_contract_id: this.treatmentContract?.id,
      });
      const { status, data } = sendApiProject;

      if (status && data.result) {
        this.wastesOrServices = data.result;
        // set default is_reservation_showing_to_discharger = 1
        this.wastesOrServices = this.wastesOrServices.map(item => {
          return {
            is_reservation_showing_to_discharger: 1,
            ...item
          }
        })

        this.wastesOrServicesByWorkOrder.forEach(item => {
          const matchWasteOrService  = this.wastesOrServices.findIndex(x => x.check_key === item.check_key)
          if (matchWasteOrService !== -1) {
            this.wastesOrServices[matchWasteOrService] = {...item};
          }
        })
      }

      this.isLoading = false;
    },
    updateValue() {
      const wastesOrServices = [...this.wastesOrServices];
      let dataSelected = wastesOrServices.filter(waste_or_service => {
        if (waste_or_service.disposal_sites_by_waste_id) {
          return this.selectedItems.includes(`${waste_or_service.cd}_${waste_or_service.disposal_sites_by_waste_id}`)
        }
        return this.selectedItems.includes(`${waste_or_service.cd}`);
      });
      this.$emit("dataSelected", [...dataSelected]);
      this.$bvModal.hide("choose-wastesOrServices");
    },
    getValueCheckbox(item) {
      const value = [];
      if (item.cd) value.push(item.cd);
      if (item.disposal_sites_by_waste_id) value.push(item.disposal_sites_by_waste_id);

      return value.join('_');
    },
    onClickRow(row) {
      const value = this.getValueCheckbox(row);
      if (this.selectedItems.includes(value)) {
        const index = this.selectedItems.indexOf(value);
        if (index > -1) {
          this.selectedItems.splice(index, 1);
        }
      } else {
        this.selectedItems.push(value);
      }
    },
  },
  computed: {
    headers() {
      return this.$constant.tableHeaders.work_orders.wastes_or_services;
    },
    transportContract(){
      return this.contracts?.find(contract => !!contract.transport_contract_cd) ?? {}
    },
    treatmentContract(){
      return this.contracts?.find(contract => !!contract.treatment_contract_cd) ?? {}
    },
  },
  watch: {
    wastesOrServicesByWorkOrder: {
      handler: function(val) {
        if (!val) return;
        this.selectedItems = val.map(item => {
          const value = [];
          if (item.cd) value.push(item.cd);
          if (item.disposal_sites_by_waste_id) value.push(item.disposal_sites_by_waste_id);

          return value.join('_');
        });
        val.forEach(item => {
          const matchWasteOrService  = this.wastesOrServices.findIndex(x => {
            return x.cd === item.cd && x.disposal_sites_by_waste_id === item.disposal_sites_by_waste_id
          })
          if (matchWasteOrService !== -1) {
            this.wastesOrServices[matchWasteOrService] = {...item};
          }
        })
      },
      deep: true,
      immediate: true,
    },
    workOrderTypeId: {
      handler: function(val) {
        if (!val && this.isUpdate) return;
        this.getData()
      },
      immediate: true,
    },
    contracts() {
      this.getData().then(()=>{
        if(this.selectedItems.length){
          this.updateValue()
        }
      })
    }
  },
};
</script>
<template>
  <div class="mb-2 d-xl-flex">
    <button
      v-b-modal.choose-wastesOrServices
      class="btn btn-primary waves-effect waves-light"
      :disabled="disabled"
    >
      <i class='far fa-list-alt'></i> 廃棄物・サービスを選択する
    </button>
    <!-- modal area -->
    <b-modal
      size="lg"
      id="choose-wastesOrServices"
      centered
      title-class="font-18"
      hide-footer
      title="廃棄物・サービス一覧"
      scrollable
      @hidden="$emit('modalHidden', 'key_wastes_or_services')"
    >
      <b-row class="mt-2">
        <b-overlay
          rounded
          opacity="0.6"
          spinner-small
          spinner-variant="primary"
          class="d-inline-block"
        >
          <CommonTableComponent :height-class="`table-wrapper-500`">
            <thead class="table-light">
              <tr>
                <th></th>
                <th
                  v-for="(item, index) in headers"
                  :key="index"
                  :class="getHeaderClass(item)"
                >
                  <span>
                    {{ item.text }}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in wastesOrServices" :key="index" @click="onClickRow(item)">
                <td>
                  <b-form-checkbox
                    :value="getValueCheckbox(item)"
                    v-model="selectedItems"
                  />
                </td>
                <td><p class="white-space-pre">{{ item.cd }}</p></td>
                <td>{{ item.name }}</td>
                <td>{{ item.waste_category_name }}</td>
                <td>{{ item.vendor_name }}</td>
                <td>{{ item.treatment_method }}</td>
              </tr>
              <tr v-if="!wastesOrServices.length">
                <td :colspan="headers.length" class="text-center">
                  データなし。
                </td>
              </tr>
            </tbody>
          </CommonTableComponent>
          <div class="d-flex justify-content-end mt-2">
            <button
              @click="updateValue"
              class="btn btn-primary w-md waves-effect waves-light"
            >
              <i class='fas fa-check'></i> 確定
            </button>
          </div>
        </b-overlay>
      </b-row>
    </b-modal>
  </div>
</template>
<style lang="scss" scoped>
p.white-space-pre {
  margin-top: 3px;
  margin-bottom: 3px;
}
</style>
